import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import moment from 'moment';
import Input from '../../../../components/form-control/Input';
import CustomerTypeaheadInput from '../../../../components/form-control/CustomerTypeaheadInput';
import DatePickerInput from '../../../../components/form-control/DatePickerInput';
import SelectMenu, { ISelectMenuItemValue } from '../../../../components/form-control/SelectMenu';
import SelectDropdown from '../../../../components/form-control/SelectDropdown';
import VerifyInput from '../../../../components/VerifyInput';
import InputSelectMenu from '../../../../components/form-control/InputSelectMenu';
import { OCCUPATION_TYPES, REGIONS_BY_COUNTRY, RIDE_SHARING_DAILY_AVERAGE_INCOME } from '../../../../resources/constants';
import { employeeIncomeBand, getIdLabel, getIdPlaceholder, getListFromRecord, sentenceCase } from '../../../../utils/miscFunctions';
import { useSelector } from 'react-redux/es/hooks/useSelector';
import { getEnumsFromState, getSelectedCountry } from '../../../../states/slices/resourceSlice';
import { CUSTOMER_TYPE } from '../../../../resources/enums';
import { ICustomerType, IEnum } from '../../../../resources/interfaces';
import { Customer } from '../../../../models/customer/Customer';
import { Applicant } from '../../../../models/applicant/Applicant';

interface CustomerInformationFormProps {
  customerData: Customer | Applicant | null;
  customerType: ICustomerType | null;
  isRideSharing?: boolean;
  isNonMotorPolicy?: boolean;
  showEmployerDetails?: boolean;
  employerDetailsRequired?: boolean;
  showCustomerType?: boolean;
  showOtherPhoneField?: boolean;
  showRegionAndDigitalAddress?: boolean;
  setSelectedCustomerTypeValue?: (value: ICustomerType) => void;
  showCustomerPhoneVerificationModal?: (phone: string) => void;
  /** Enable inline typeahead on the First Name and Phone fields. When the
   * broker picks a suggestion, the entire form is pre-filled and the parent
   * is notified via {@link onCustomerSelected}. */
  enableCustomerLookup?: boolean;
  onCustomerSelected?: (customer: Customer) => void;
}

const CustomerInformationForm: React.FC<CustomerInformationFormProps> = ({
  customerData,
  customerType,
  isRideSharing,
  isNonMotorPolicy = false,
  showEmployerDetails = false,
  employerDetailsRequired=false,
  showOtherPhoneField = true,
  showRegionAndDigitalAddress = false,
  setSelectedCustomerTypeValue,
  showCustomerPhoneVerificationModal,
  enableCustomerLookup = false,
  onCustomerSelected,
}) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = useFormikContext<any>();
  const [selectedCustomerType, setSelectedCustomerType] = useState<ICustomerType | null>(customerType || values.customerType || null);
  const selectedCountry = useSelector(getSelectedCountry);
  const enums = useSelector(getEnumsFromState);
  const idTypes = enums ? enums?.idTypes : [];
  const gender: IEnum[] = enums?.genders;
  const countryId = selectedCountry?.id ?? 1;
  const customerTypes = ['BUSINESS', 'INDIVIDUAL'];
  const isBusiness = selectedCustomerType === CUSTOMER_TYPE.BUSINESS;
  const isIndividualCustomer = selectedCustomerType === CUSTOMER_TYPE.INDIVIDUAL;
  const OCCUPATIONS = getListFromRecord(OCCUPATION_TYPES, 'title');
  const idTypesList = isBusiness ?
    [{
      name: 'TIN',
      value: 'TIN'
    }] : isIndividualCustomer ? idTypes?.filter((type: any) => (type.key === 'PASSPORT' || type.key === 'NATIONAL_ID'))?.map((type: any) => ({
      name: type.title,
      value: type.key
    })) : idTypes?.filter((type:any)=>(type.key === 'PASSPORT' || type.key === 'NATIONAL_ID'))?.map((type: any) => ({
      name: type.title,
      value: type.key
    }))

  useEffect(() => {
    setSelectedCustomerType(customerType)
    setFieldValue('customerType', customerType)
  }, [customerType])

  const applyPickedCustomer = (customer: Customer) => {
    if (!customer) return;
    if (customer.customerType) {
      setSelectedCustomerType(customer.customerType as ICustomerType);
      setFieldValue('customerType', customer.customerType);
    }
    setFieldValue('firstname', customer.firstName ?? '');
    setFieldValue('lastname', customer.lastName ?? '');
    setFieldValue('phone', customer.phone ?? '');
    setFieldValue('emailAddress', customer.email ?? '');
    setFieldValue('gender', customer.gender ?? '');
    setFieldValue('dateOfBirth', customer.dateOfBirth ?? '');
    setFieldValue('occupation', customer.occupation ?? '');
    setFieldValue('address', customer.address ?? '');
    setFieldValue('digitalAddress', customer.digitalAddress ?? '');
    setFieldValue('region', customer.state ?? '');
    setFieldValue('state', customer.state ?? '');
    setFieldValue('idType', customer.idType ?? '');
    setFieldValue('idNumber', customer.idNumber ?? '');
    setFieldValue('idExpiry', customer.idExpiryDate ?? '');
    setFieldValue('incorporationDate', customer.incorporationDate ?? '');
    setFieldValue('otherPhone', customer.otherPhone ?? '');
    setFieldValue('businessName', (customer as any).businessName ?? '');
    if (customer.id) setFieldValue('id', customer.id);
    onCustomerSelected?.(customer);
  };

  // Render business-specific fields
  const renderBusinessFields = () => (
    isBusiness && (
      <>
        <Input
          type={'text'}
          name="businessName"
          value={values.businessName}
          onChange={handleChange}
          onBlur={handleBlur}
          label="Business Name"
          placeholder="Enter business name"
          required
          error={touched.businessName ? errors.businessName : undefined}
          helperText={touched?.businessName ? errors.businessName : undefined}
        />

        <DatePickerInput
          id="incorporationDate"
          selected={values.incorporationDate ? new Date(values?.incorporationDate) : null}
          onChange={(date: Date | null) => { setFieldValue("incorporationDate", date) }}
          label="Date of Incorporation"
          placeholder="Select date"
          error={touched.incorporationDate && errors.incorporationDate ? String(errors.incorporationDate) : undefined}
          helperText={touched?.incorporationDate && errors.incorporationDate ? String(errors.incorporationDate) : undefined}
          // required
        />
      </>
    )
  );

  // Render individual-specific fields
  const renderIndividualFields = () => (
    isIndividualCustomer && (
      <>
        <DatePickerInput
          id="dateOfBirth"
          selected={values.dateOfBirth ? new Date(values.dateOfBirth) : null}
          onChange={(date: Date | null) => {
            setFieldValue('dateOfBirth', date);
          }}
          label="Date of birth"
          placeholder="Select date of birth"
          maxDate={moment().subtract(18, 'years').toDate()}
          required
          error={touched.dateOfBirth ? errors.dateOfBirth as any : undefined}
          helperText={touched?.dateOfBirth ? errors.dateOfBirth as any : undefined}
        />
      </>
    )
  );

  // Render ride-sharing specific fields
  const renderRideSharingFields = () => (
    isRideSharing ? (
      <>
        <SelectMenu
          label="Daily Average Income"
          name="monthlyIncome"
          listSelectedValue={values.monthlyIncome}
          onChange={(val: ISelectMenuItemValue | null) => setFieldValue('monthlyIncome', val)}
          search={false}
          list={RIDE_SHARING_DAILY_AVERAGE_INCOME}
          placeholder="Select average daily income"
          error={touched.monthlyIncome ? errors.monthlyIncome : undefined}
          helperText={touched?.monthlyIncome ? errors.monthlyIncome : undefined}
        />

        <Input
          value={values.rideSharePeriod}
          name="rideSharePeriod"
          label="How long have you been doing the ride share business?"
          placeholder="Eg. 5"
          append={values?.rideSharePeriod > 1 ? 'yrs' : 'yr'}
          type="number"
          onChange={handleChange}
          onBlur={handleBlur}
        />

        <SelectDropdown
          name="liability"
          label="Do you have any other liabilities?"
          list={[
            { name: 'Yes', value: 'yes' },
            { name: 'No', value: 'no' }
          ]}
          listSelectedValue={values.liability}
          onChange={(val: string | null) => {
            setFieldValue('liability', val);
          }}
          onBlur={handleBlur}
          error={touched.liability ? errors.liability : undefined}
          required
          sort
          helperText={touched?.liability ? errors.liability : undefined}
        />

        {values?.liability === 'yes' && (
          <>
            <Input
              name="liabilityDetails"
              value={values.liabilityDetails}
              label="What other liabilites do you have?"
              placeholder="Eg. Quick Loan, MTN Loan"
              type="text"
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.liabilityDetails ? errors.liabilityDetails : undefined}
              helperText={touched?.liabilityDetails ? errors.liabilityDetails : undefined}
            />

            <Input
              name="liabilityDebt"
              value={values.liabilityDebt}
              label="How much are you owing?"
              placeholder="Eg: 5000"
              prepend={'GHc'}
              type="number"
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.liabilityDebt ? errors.liabilityDebt : undefined}
              helperText={touched?.liabilityDebt ? errors.liabilityDebt : undefined}
            />

            <Input
              name="liabilityModeOfPayment"
              value={values.liabilityModeOfPayment}
              label="How are you paying for the extra liabities?"
              placeholder=""
              type="text"
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.liabilityModeOfPayment ? errors.liabilityModeOfPayment : undefined}
              helperText={touched?.liabilityModeOfPayment ? errors.liabilityModeOfPayment : undefined}
            />
          </>
        )}
      </>
    ) : (
      // Regular employee fields
      showEmployerDetails && <>
        <Input
          value={values.employerName}
          name="employerName"
          label="Employer's Name"
          placeholder="Employer's Name"
          type="text"
          onChange={handleChange}
          onBlur={handleBlur}
          required={employerDetailsRequired}
          error={touched?.employerName ? errors.employerName : undefined}
          helperText={touched?.employerName ? errors.employerName : undefined}
        />

        <Input
          value={values.employerAddress}
          name="employerAddress"
          label="Employer's Address"
          placeholder="Employer's Address"
          type="text"
          onChange={handleChange}
          onBlur={handleBlur}
          required={employerDetailsRequired}
          error={touched?.employerAddress ? errors.employerAddress : undefined}
          helperText={touched?.employerAddress ? errors.employerAddress : undefined}
        />

        <Input
          value={values.employerPhone}
          name="employerPhone"
          label="Employer's Contact Number"
          placeholder="Eg. 0245000000"
          type="tel"
          onChange={handleChange}
          onBlur={handleBlur}
          required={employerDetailsRequired}
          error={touched?.employerPhone ? errors.employerPhone : undefined}
          helperText={touched?.employerPhone ? errors.employerPhone : undefined}
        />

        <SelectMenu
          label="Monthly Income Band"
          name="monthlyIncome"
          listSelectedValue={values.monthlyIncome}
          onChange={(val: ISelectMenuItemValue | null) => setFieldValue('monthlyIncome', val)}
          search={false}
          searchPlaceHolder="Search by Income Band ..."
          list={employeeIncomeBand(selectedCountry.currencySymbol, countryId)}
          placeholder="Select Monthly Income band"
          required={employerDetailsRequired}
          error={touched.monthlyIncome ? errors.monthlyIncome : undefined}
          helperText={touched?.monthlyIncome ? errors.monthlyIncome : undefined}
        />
      </>
    )
  );

  return (
    <div className="space-y-8 md:space-y-0 md:grid md:grid-cols-3 md:gap-4">

      {/* Customer Type */}

      <SelectMenu
        name="customerType"
        label="Customer Type"
        listSelectedValue={values.customerType}
        onChange={(val: ICustomerType | null) => {
          setFieldValue('customerType', val);
          val && setSelectedCustomerType(val);
          val && setSelectedCustomerTypeValue?.(val);
          val === 'BUSINESS' ? setFieldValue('idType', 'TIN') : setFieldValue('idType', '');
        }}
        onBlur={handleBlur}
        list={customerTypes}
        placeholder="Select customer type"
        emptyListMessage="Select relationship to proceed"
        required
        error={touched.customerType ? errors.customerType : undefined}
        helperText={touched?.customerType ? errors.customerType : undefined}
      />

      {/* First Name */}
      {enableCustomerLookup ? (
        <CustomerTypeaheadInput
          name="firstname"
          label={isBusiness ? 'Contact Person First Name' : 'First Name'}
          placeholder="Eg: Jay"
          value={values?.firstname ?? ''}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          error={touched?.firstname ? errors.firstname : undefined}
          helperText={touched?.firstname ? errors.firstname : undefined}
          onCustomerPicked={applyPickedCustomer}
        />
      ) : (
        <Input
          type="text"
          name="firstname"
          label={isBusiness ? 'Contact Person First Name' : 'First Name'}
          placeholder="Eg: Jay"
          value={values?.firstname}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          error={touched?.firstname ? errors.firstname : undefined}
          helperText={touched?.firstname ? errors.firstname : undefined}
        />
      )}

      {/* Last Name */}
      <Input
        type="text"
        name="lastname"
        label={isBusiness ? 'Contact Person Last Name' : 'Last Name'}
        placeholder="Eg: Walker"
        value={values?.lastname}
        onChange={handleChange}
        onBlur={handleBlur}
        required
        error={touched?.lastname ? errors.lastname : undefined}
        helperText={touched?.lastname ? errors.lastname : undefined}
      />

      {/* Phone Number */}
      {enableCustomerLookup ? (
        <CustomerTypeaheadInput
          name="phone"
          label="Phone Number"
          value={values?.phone ?? ''}
          placeholder="Enter phone number"
          onChange={handleChange}
          required
          labelClasses="!text-gray-700 dark:!text-darkBg-mainText w-full"
          error={touched.phone ? errors.phone : undefined}
          helperText={touched?.phone ? errors.phone : undefined}
          onCustomerPicked={applyPickedCustomer}
        />
      ) : (
        <Input
          type="text"
          name="phone"
          label="Phone Number"
          value={values.phone}
          placeholder="Enter phone number"
          onChange={handleChange}
          required
          labelClasses="!text-gray-700 dark:!text-darkBg-mainText w-full"
          error={touched.phone ? errors.phone : undefined}
          helperText={touched?.phone ? errors.phone : undefined}
        />
      )}

      {/* Gender */}
      {!isNonMotorPolicy &&
        <SelectDropdown
          label="Gender"
          name="gender"
          listSelectedValue={values.gender}
          list={gender?.map((g) => ({
            name: g.title,
            value: g.key,
            description: g.description
          }))}
          onChange={(val: string | null) => setFieldValue('gender', val)}
          onBlur={handleBlur}
          placeholder="Select gender"
          error={touched.gender ? errors.gender : undefined}
          helperText={touched.gender ? errors.gender : undefined}
          // required
          wrapperClasses="w-full"
        />}

      {/* Business or Individual Fields */}
      {renderBusinessFields()}
      {!isNonMotorPolicy && renderIndividualFields()}

      {/* ID Type */}
      <SelectDropdown
        label="ID Type"
        name="idType"
        listSelectedValue={values.idType}
        onChange={(val: any) => {
          setFieldValue('idType', val);
        }}
        list={idTypesList}
        placeholder="Select ID Type"
        required
        error={touched.idType ? errors.idType : undefined}
        helperText={touched?.idType ? errors.idType : undefined}
      />

      {/* ID Number */}
      <Input
        value={values.idNumber}
        name="idNumber"
        label={getIdLabel(values.idType)}
        placeholder={getIdPlaceholder(values.idType, countryId)}
        type="text"
        onChange={handleChange}
        onBlur={handleBlur}
        required
        error={touched.idNumber ? errors.idNumber : undefined}
        helperText={touched?.idNumber ? errors.idNumber : undefined}
      />

      {/* ID Expiry (for individuals) */}
      {isIndividualCustomer && (
        <DatePickerInput
          id="idExpiry"
          selected={values.idExpiry ? new Date(values.idExpiry) : null}
          onChange={(date: Date | null) => {
            setFieldValue('idExpiry', date);
          }}
          allowEarlierSelections={false}
          label="ID Expiry Date"
          required
          placeholder="Select expiry date"
          error={touched.idExpiry ? errors.idExpiry as any : undefined}
          helperText={touched?.idExpiry ? errors.idExpiry as any : undefined}
        />
      )}

      {/* Address */}
      <Input
        type="text"
        name="address"
        label="Address"
        placeholder="Eg: No. 12 Kpong Street, Achimota"
        value={values?.address ?? ''}
        onChange={handleChange}
        onBlur={handleBlur}
        // required
        error={touched?.address ? errors.address : undefined}
        helperText={touched?.address ? errors.address : undefined}
      />

      {/* Occupation */}
      {!isNonMotorPolicy && <InputSelectMenu
        label="Occupation"
        name="occupation"
        listSelectedValue={values.occupation}
        onChange={(val: string | null) => {
          setFieldValue('occupation', val);
        }}
        onBlur={handleBlur}
        list={OCCUPATIONS}
        placeholder="Select protection type"
        sort
        search
        // required
        emptyListMessage="Select occupation to proceed"
        error={touched.occupation ? errors.occupation : undefined}
        helperText={touched?.occupation ? errors.occupation : undefined}
      />}

      {/* Email */}
      <Input
        type="text"
        id="emailAddress"
        label="Email"
        placeholder="Eg: someone@domain.com"
        className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border"
        value={values?.emailAddress ?? ''}
        onChange={handleChange}
        onBlur={handleBlur}
      />

      {/* Other Phone */}
      {!isNonMotorPolicy || showOtherPhoneField &&
        <VerifyInput
          label="Other Phone"
          name="otherPhone"
          id={'otherPhone'}
          readOnly={customerData?.otherPhoneVerified}
          verified={customerData?.otherPhoneVerified || false}
          value={values.otherPhone}
          onChange={handleChange}
          onClick={() =>
            values?.otherPhone &&
            showCustomerPhoneVerificationModal?.(values?.otherPhone)
          }
          isVerifiedInput={false}
        />}



      {/* Conditional Fields (Ride Sharing or Regular) */}
      {renderRideSharingFields()}

      {showRegionAndDigitalAddress && <>
        <Input
          type={'text'}
          name="digitalAddress"
          value={values.digitalAddress}
          onChange={handleChange}
          onBlur={handleBlur}
          label="Digital Address"
          placeholder='Enter digital address'
          required
          error={touched.digitalAddress ? errors.digitalAddress : undefined}
          helperText={touched?.digitalAddress ? errors.digitalAddress : undefined}
          className="rounded"
        />

        <SelectMenu
          label="Region"
          name="state"
          listSelectedValue={sentenceCase(values?.state ?? '')}
          onChange={(val: ISelectMenuItemValue | null) => {
            setFieldValue('state', val);
          }}
          onBlur={handleBlur}
          search
          searchPlaceHolder="Search by state ..."
          list={REGIONS_BY_COUNTRY[selectedCountry.iso]}
          placeholder="Select region"
          error={touched.state ? errors.state : undefined}
          helperText={touched?.state ? errors.state : undefined}
          required
        />


      </>}
    </div>
  );
};

export default CustomerInformationForm;